import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AffiliateDisclosure from '@/components/feature/AffiliateDisclosure';
import SEOHead from '@/components/feature/SEOHead';
import { blogPosts } from '@/mocks/blogs';
import { products } from '@/mocks/products';

const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'MeowCentral Cat Blog',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog`,
  description: 'Expert cat care tips, behavior guides, nutrition advice, and fun facts for cat lovers. Written by vets and cat behaviorists.',
  publisher: {
    '@type': 'Organization',
    name: 'MeowCentral',
    logo: {
      '@type': 'ImageObject',
      url: 'https://public.readdy.ai/ai/img_res/37bc7df8-b441-456a-b64e-dfe2e72930b6.png',
    },
  },
};

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Posts', icon: 'ri-article-line' },
    { id: 'care', name: 'Cat Care', icon: 'ri-heart-pulse-line' },
    { id: 'behavior', name: 'Behavior', icon: 'ri-emotion-line' },
    { id: 'health', name: 'Health', icon: 'ri-medicine-bottle-line' },
    { id: 'nutrition', name: 'Nutrition', icon: 'ri-restaurant-2-line' },
    { id: 'fun', name: 'Fun Facts', icon: 'ri-lightbulb-line' },
  ];

  const filteredBlogs = blogPosts.filter(
    (blog) => selectedCategory === 'all' || blog.category.toLowerCase() === selectedCategory
  );

  const getRelatedProducts = (productIds: number[]) => {
    return products.filter(p => productIds.includes(Number(p.id))).slice(0, 3);
  };

  const getRetailerIcon = (retailer: string) => {
    switch(retailer) {
      case 'amazon': return 'ri-amazon-fill';
      case 'chewy': return 'ri-shopping-bag-3-fill';
      default: return 'ri-store-2-fill';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-teal-50">
      <SEOHead
        title="Cat Blog - Expert Tips, Care Guides & Fun Facts | MeowCentral"
        description="Read expert cat care tips, behavior guides, nutrition advice, and fun facts. MeowCentral's blog covers everything from kitten care to senior cat health — written by vets and cat lovers."
        keywords="cat blog, cat care tips, cat behavior, cat nutrition, cat health, kitten care, cat advice, cat guides"
        canonical="/blog"
        schema={blogSchema}
      />
      <Navbar />
      <AffiliateDisclosure />
      
      {/* Header Section */}
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-3">
              Cat Blog 📝
            </h1>
            <p className="text-lg text-gray-600">
              Expert tips, guides, and stories for cat lovers
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full whitespace-nowrap transition-all ${
                  selectedCategory === category.id
                    ? 'bg-teal-500 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-teal-50 shadow'
                }`}
              >
                <i className={`${category.icon} text-xl`}></i>
                <span className="font-medium">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="px-4 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog) => {
              const relatedProducts = getRelatedProducts(blog.relatedProducts || []);
              
              return (
                <article
                  key={blog.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group flex flex-col"
                >
                  <Link to={`/blog/${blog.slug}`} className="relative overflow-hidden block cursor-pointer">
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-56 object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 bg-teal-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                      {blog.category}
                    </div>
                  </Link>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        <span>{new Date(blog.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                    </div>
                    <Link to={`/blog/${blog.slug}`}>
                      <h2 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2 group-hover:text-teal-600 transition-colors cursor-pointer">
                        {blog.title}
                      </h2>
                    </Link>
                    <p className="text-gray-600 mb-4 line-clamp-3 flex-1">
                      {blog.excerpt}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-amber-400 flex items-center justify-center text-white font-bold">
                          {blog.author.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{blog.author}</p>
                        </div>
                      </div>
                      <Link 
                        to={`/blog/${blog.slug}`}
                        className="text-teal-500 font-medium hover:text-teal-600 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
                      >
                        Read More
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>

                    {/* Shop the Products Section */}
                    {relatedProducts.length > 0 && (
                      <div className="border-t border-amber-100 pt-4 mt-auto">
                        <div className="flex items-center gap-2 mb-3">
                          <i className="ri-shopping-cart-2-line text-amber-600"></i>
                          <h3 className="text-sm font-bold text-gray-800">Shop Products in This Post</h3>
                        </div>
                        <div className="space-y-2">
                          {relatedProducts.map((product) => (
                            <a
                              key={product.id}
                              href={product.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 p-2 rounded-lg bg-gradient-to-r from-amber-50 to-teal-50 hover:from-amber-100 hover:to-teal-100 transition-all group/product cursor-pointer"
                            >
                              <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-white">
                                <img 
                                  src={product.image} 
                                  alt={product.name}
                                  className="w-full h-full object-cover object-top"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-gray-800 line-clamp-1 group-hover/product:text-teal-600 transition-colors">
                                  {product.name}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-sm font-bold text-teal-600">${product.price}</span>
                                  <div className="flex items-center gap-1">
                                    <i className={`${getRetailerIcon(product.retailer)} text-xs text-gray-500`}></i>
                                    <span className="text-xs text-gray-500 capitalize">{product.retailer}</span>
                                  </div>
                                </div>
                              </div>
                              <i className="ri-arrow-right-line text-teal-500 group-hover/product:translate-x-1 transition-transform"></i>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}